import { type MouseEvent, useState } from 'react';
import { HISTORY_DEFAULT_PAGE_SIZE } from './EntityDailyHistoryPanel.constants';
import type { DailyHistorySortKey } from './EntityDailyHistoryPanel.types';

type UseEntityDailyHistoryPanelControllerArgs = {
  controlledExpandedRowIds?: Set<string>;
  controlledPage?: number;
  controlledPageSize?: number;
  controlledSortDirection?: 'asc' | 'desc';
  controlledSortKey?: DailyHistorySortKey;
  controlledTotalCount?: number;
  defaultSortDirection: 'asc' | 'desc';
  onControlledPageChange?: (page: number) => void;
  onControlledPageSizeChange?: (pageSize: number) => void;
  onControlledSortChange?: (key: DailyHistorySortKey, direction: 'asc' | 'desc') => void;
  onExpandedRowIdsChange?: (rowIds: Set<string>) => void;
  preserveWindowScroll: (action: () => void) => void;
  resolvedDefaultSortKey: DailyHistorySortKey;
};

type UseEntityDailyHistoryPanelControllerResult = {
  closeSortPopover: () => void;
  expandedRowIds: Set<string>;
  handlePageChange: (_event: unknown, nextPage: number) => void;
  handlePageSizeChange: (nextPageSize: number) => void;
  handleReset: () => void;
  handleSortButtonClick: (event: MouseEvent<HTMLButtonElement>) => void;
  handleSortChange: (nextSortKey: DailyHistorySortKey, nextSortDirection: 'asc' | 'desc') => void;
  hasActiveSort: boolean;
  isPaginationControlled: boolean;
  page: number;
  pageSize: number;
  setExpandedRowIds: (rowIds: Set<string>) => void;
  sortAnchorEl: HTMLElement | null;
  sortDirection: 'asc' | 'desc';
  sortKey: DailyHistorySortKey;
  toggleRowExpansion: (rowId: string) => void;
};

export const useEntityDailyHistoryPanelController = ({
  controlledExpandedRowIds,
  controlledPage,
  controlledPageSize,
  controlledSortDirection,
  controlledSortKey,
  controlledTotalCount,
  defaultSortDirection,
  onControlledPageChange,
  onControlledPageSizeChange,
  onControlledSortChange,
  onExpandedRowIdsChange,
  preserveWindowScroll,
  resolvedDefaultSortKey,
}: UseEntityDailyHistoryPanelControllerArgs): UseEntityDailyHistoryPanelControllerResult => {
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [internalExpandedRowIds, setInternalExpandedRowIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [internalPage, setInternalPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(HISTORY_DEFAULT_PAGE_SIZE);
  const [internalSortKey, setInternalSortKey] =
    useState<DailyHistorySortKey>(resolvedDefaultSortKey);
  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>(
    defaultSortDirection,
  );

  const isPaginationControlled =
    controlledPage !== undefined &&
    controlledPageSize !== undefined &&
    controlledTotalCount !== undefined &&
    onControlledPageChange !== undefined &&
    onControlledPageSizeChange !== undefined;
  const isSortControlled =
    controlledSortKey !== undefined &&
    controlledSortDirection !== undefined &&
    onControlledSortChange !== undefined;

  const page = isPaginationControlled ? controlledPage : internalPage;
  const pageSize = isPaginationControlled ? controlledPageSize : internalPageSize;
  const sortKey = isSortControlled ? controlledSortKey : internalSortKey;
  const sortDirection = isSortControlled ? controlledSortDirection : internalSortDirection;
  const expandedRowIds = controlledExpandedRowIds ?? internalExpandedRowIds;

  const hasActiveSort =
    sortKey !== resolvedDefaultSortKey || sortDirection !== defaultSortDirection;

  const closeSortPopover = (): void => {
    setSortAnchorEl(null);
  };

  const handleSortButtonClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortChange = (
    nextSortKey: DailyHistorySortKey,
    nextSortDirection: 'asc' | 'desc',
  ): void => {
    preserveWindowScroll(() => {
      if (isSortControlled) {
        onControlledSortChange(nextSortKey, nextSortDirection);
      } else {
        setInternalSortKey(nextSortKey);
        setInternalSortDirection(nextSortDirection);
      }

      if (isPaginationControlled) {
        onControlledPageChange(0);
      } else {
        setInternalPage(0);
      }

      closeSortPopover();
    });
  };

  const handleReset = (): void => {
    preserveWindowScroll(() => {
      if (isSortControlled) {
        onControlledSortChange(resolvedDefaultSortKey, defaultSortDirection);
      } else {
        setInternalSortKey(resolvedDefaultSortKey);
        setInternalSortDirection(defaultSortDirection);
      }

      if (isPaginationControlled) {
        onControlledPageChange(0);
      } else {
        setInternalPage(0);
      }

      closeSortPopover();
    });
  };

  const toggleRowExpansion = (rowId: string): void => {
    preserveWindowScroll(() => {
      const nextExpandedRowIds = new Set(expandedRowIds);
      if (nextExpandedRowIds.has(rowId)) {
        nextExpandedRowIds.delete(rowId);
      } else {
        nextExpandedRowIds.add(rowId);
      }

      if (controlledExpandedRowIds === undefined) {
        setInternalExpandedRowIds(nextExpandedRowIds);
      }

      onExpandedRowIdsChange?.(nextExpandedRowIds);
    });
  };

  const setExpandedRowIds = (rowIds: Set<string>): void => {
    preserveWindowScroll(() => {
      const nextExpandedRowIds = new Set(rowIds);

      if (controlledExpandedRowIds === undefined) {
        setInternalExpandedRowIds(nextExpandedRowIds);
      }

      onExpandedRowIdsChange?.(nextExpandedRowIds);
    });
  };

  const handlePageChange = (_event: unknown, nextPage: number): void => {
    preserveWindowScroll(() => {
      if (isPaginationControlled) {
        onControlledPageChange(nextPage);
        return;
      }

      setInternalPage(nextPage);
    });
  };

  const handlePageSizeChange = (nextPageSize: number): void => {
    preserveWindowScroll(() => {
      if (isPaginationControlled) {
        onControlledPageSizeChange(nextPageSize);
        onControlledPageChange(0);
        return;
      }

      setInternalPageSize(nextPageSize);
      setInternalPage(0);
    });
  };

  return {
    closeSortPopover,
    expandedRowIds,
    handlePageChange,
    handlePageSizeChange,
    handleReset,
    handleSortButtonClick,
    handleSortChange,
    hasActiveSort,
    isPaginationControlled,
    page,
    pageSize,
    setExpandedRowIds,
    sortAnchorEl,
    sortDirection,
    sortKey,
    toggleRowExpansion,
  };
};
